import {
  ALL_MODULES,
  getTranslatedText,
  ENTITY_MAP,
  COMMON_CONSTANTS
} from '@nile/components';
import { WORKLOG_ITEM_STATE } from '@nile/components/src/Constants/worklog';
import {
  AccountClient,
  getApiMethodAndKeyNames,
  apiResolver,
  GET_FIELD,
  GET_REQUESTERS_BY_CRITERIA,
  GET_TECHNICIANS_BY_CRITERIA,
  TicketClient,
  GET_SERVICE_ITEMS
} from '@nile/graphql';
import { includes, isEmpty } from 'lodash';

/**
 * For entity type, if we want to call a specif api, this method will be called.
 * @param {object} item
 */
function getQuery(item) {
  const entity = item?.name?.split('.')[0] || '';
  switch (entity) {
    case 'technician':
    case 'followers':
      return GET_TECHNICIANS_BY_CRITERIA;
    case 'serviceItem':
      return GET_SERVICE_ITEMS;
    default:
      return GET_REQUESTERS_BY_CRITERIA;
  }
}

/**
 * After getting values from the API, this value specifies
 * the method name to process the data
 * @param {object} item
 */
function getNameKey(item) {
  const entity = item?.split('.')[0] || '';
  switch (entity) {
    case 'technician':
    case 'followers':
      return 'getTechnicianList';
    case 'serviceItem':
      return 'getServiceItems';
    default:
      return 'getRequesterList';
  }
}

function formatOperatorValues(allOperators, operator, intl) {
  return allOperators.getRuleBuilderOperators.operator_placeholders[
    operator
  ]?.map((el) => {
    return {
      ...el,
      label: getTranslatedText(intl, el.key),
      value: getTranslatedText(intl, el.key),
      name: el.key
    };
  });
}

/**
 * This method gives the query and client using which the API calls will be made
 * after selecting the attribute
 * @param {object} item
 * @param {String} operator
 */

function getPropValues(item, operator, allOperators, intl) {
  if (item?.name) {
    if (item.isCustom) {
      if (includes(['date', 'datetime'], item.dataType) && operator) {
        return formatOperatorValues(allOperators, operator, intl);
      }
      return AccountClient.query({
        query: GET_FIELD,
        variables: {
          input: {
            columnName: item?.name || item?.columnName,
            module: ALL_MODULES.ticket
          }
        }
      });
    }
    const query = apiResolver.OfferedItem[item.name]?.api;
    const client = apiResolver.OfferedItem[item.name]?.client;

    let variables = apiResolver.OfferedItem[item.name]?.variables || {};
    switch (item.name) {
      case 'technician.name':
      case 'technician.email':
        variables = {
          input: {
            listInfo: {
              sort: {
                attribute: 'name',
                order: 'ASC'
              }
            }
          }
        };
        break;
      case 'client.name':
        variables = {
          listInfo: {
            sort: {
              attribute: 'name',
              order: 'ASC'
            }
          }
        };
        break;
      default:
        variables = apiResolver.OfferedItem[item.name]?.variables || {};
        break;
    }
    if (query && client) {
      return client.query({
        query: query,
        variables: variables
      });
    }
    if (operator) {
      return formatOperatorValues(allOperators, operator, intl);
    }
    return { client: AccountClient, query: getQuery(item) };
  }
  return {};
}

async function onOfferedItemAttributeSelect(params) {
  const { item: field, operator, fields, allOperators, intl } = params;
  const allFields = fields;
  const selectedField = allFields.find((el) => el.name === field);

  if (
    selectedField?.name === 'serviceCatalogItem.serviceTypeItem.offeringType'
  ) {
    return [
      {
        name: 'BLOCK_HOUR',
        value: 'BLOCK_HOUR',
        label: getTranslatedText(intl, 'admin.serviceCatalog.blockHour')
      },
      {
        name: 'BLOCK_MONEY',
        value: 'BLOCK_MONEY',
        label: getTranslatedText(intl, 'admin.serviceCatalog.blockMoney')
      },
      {
        name: 'SERVICE_BUNDLE',
        value: 'SERVICE_BUNDLE',
        label: getTranslatedText(intl, 'admin.serviceCatalog.serviceBundle')
      },
      {
        name: 'QUANTITY',
        value: 'QUANTITY',
        label: getTranslatedText(intl, 'common.quantity')
      },
      {
        name: 'HOURS',
        value: 'HOURS',
        label: getTranslatedText(intl, 'metric.quantity.hours')
      }
    ];
  }
  if (selectedField?.name === 'serviceCatalogItem.itemType') {
    return [
      {
        name: 'PRODUCT',
        value: 'PRODUCT',
        label: getTranslatedText(
          intl,
          'admin.product.and.services.items.type.product'
        )
      },
      {
        name: 'SERVICE',
        value: 'SERVICE',
        label: getTranslatedText(
          intl,
          'admin.product.and.services.items.type.service'
        )
      }
    ];
  }

  if (selectedField?.name === 'serviceItem.quantityType') {
    return [
      { name: 'UNITS', value: 'UNITS', label: 'UNITS' },
      { name: 'HOURS', value: 'HOURS', label: 'HOURS' },
      { name: 'BLOCK_HOURS', value: 'BLOCK_HOURS', label: 'BLOCK_HOURS' },
      { name: 'BLOCK_MONEY', value: 'BLOCK_MONEY', label: 'BLOCK_MONEY' }
    ];
  }

  if (selectedField?.name === 'status') {
    return Object.values(WORKLOG_ITEM_STATE).map((state) => ({
      name: state,
      label: state,
      value: state
    }));
  }

  if (selectedField?.dataType === 'boolean') {
    return [
      {
        name: 'true',
        label: 'true',
        value: true
      },
      {
        name: 'false',
        label: 'false',
        value: false
      }
    ];
  }

  if (field.split('.')?.length > 1) {
    const ent = field.split('.')[0];
    if (
      ent !== 'site' &&
      ent !== 'client' &&
      ent !== 'serviceItem' &&
      ent !== 'serviceCatalogItem'
    )
      return getPropValues(selectedField, null, allOperators);
  }

  const allValues = await getPropValues(
    selectedField,
    operator,
    allOperators,
    intl
  );
  let res = [];

  if (selectedField?.name) {
    const apiMethodAndKeyNames = getApiMethodAndKeyNames(
      ENTITY_MAP['offered-item']
    );
    let method = apiMethodAndKeyNames[selectedField.name]?.method;
    const key = apiMethodAndKeyNames[selectedField.name]?.key;
    const nestedKey = apiMethodAndKeyNames[selectedField.name]?.nestedKey;
    if (selectedField.nestedTo) {
      const nestedProp = apiMethodAndKeyNames[selectedField.name]?.nestedKey;
      if (!dataToMap && !method) {
        dataToMap = allValues.data?.getField?.options;
      }
      dataToMap.forEach((el) => {
        if (el[nestedProp]) {
          res = res.concat(
            el[nestedProp].map((c) => {
              return {
                name: c[key],
                value: c[key],
                label: c[key]
              };
            })
          );
        } else {
          res = res.concat([
            { name: el?.value, value: el?.value, label: el?.value }
          ]);
        }
      });
    } else if (operator === 'on') {
      res = allValues;
    } else if (selectedField.isCustom) {
      method = 'getField';
      const fldOptions =
        allValues?.data?.[method]?.options ||
        allValues?.data?.[method]?.fieldOptions;
      res = allValues?.data
        ? fldOptions?.map((el) => {
            return {
              name: el?.value,
              value: el?.value,
              label: el?.value
            };
          })
        : [];
    } else if (nestedKey) {
      res = allValues?.data?.[method]?.[nestedKey]
        ? allValues.data?.[method]?.[nestedKey].map((el) => {
            return {
              name: el[key],
              value: el[key],
              label: el[key]
            };
          })
        : [];
    } else {
      res = allValues?.data
        ? allValues.data[method].map((el) => {
            return {
              name: key ? el[key] : el,
              value: key ? el[key] : el,
              label: key ? el[key] : el
            };
          })
        : [];
    }
  }
  return res;
}

/**
 * This method is called for autocomplete fields.
 * Usually apis will be called when we choose attributes. But for autocomplete,
 * on typing few characters, api will be called. This is the method.
 * @param {object} client
 * @param {gql} query
 * @param {object} params
 */
const onOfferedItemAttributeSearch = async (c, q, params) => {
  const field = params?.field;
  const query = apiResolver.OfferedItem[field]?.api;
  const client = apiResolver.OfferedItem[field]?.client;
  const attribute = params?.field?.split('.')[1];
  const nameKey = getNameKey(params.field);
  const variables = {
    listInfo: {
      condition: {
        joinOperator: 'AND',
        operands: [
          {
            attribute: attribute || 'firstName',
            operator: 'startsWith',
            value: params.inputValue
          }
        ]
      }
    }
  };
  const res = await client.query({
    query: query,
    variables: COMMON_CONSTANTS.IS_INSIDE_INPUT_VARIABLES[nameKey]
      ? { input: variables }
      : variables
  });
  const nestedKey = params?.field === 'serviceItem.name' ? 'items' : 'userList';
  const allOptions = !isEmpty(res?.data?.[nameKey])
    ? res.data[nameKey][nestedKey].map((el) => {
        return {
          name: `${el[attribute]}`,
          value: `${el[attribute]}`,
          label: `${el[attribute]}`
        };
      })
    : [];
  return allOptions;
};

export { onOfferedItemAttributeSelect, onOfferedItemAttributeSearch };
